/**
 * Hopelapp Dashboard - Light Theme Colors
 * Basé sur la charte graphique Hopelapp
 */

import { hopelappPalette } from "assets/theme/hopelapp-palette";

// types
interface ColorsTypes {
  main: string;
  focus: string;
}

interface GradientsTypes {
  main: string;
  state: string;
}

interface SocialMediaColorsTypes {
  main: string;
  dark: string;
}

interface BadgeColorsTypes {
  background: string;
  text: string;
}

interface HopelappBrandTypes {
  main: string;
  light: string;
  dark: string;
  contrastText: string;
}

interface Types {
  background:
    | {
        default: string;
        sidenav?: string;
        card?: string;
        paper?: string;
        hover?: string;
      }
    | any;
  white:
    | {
        main: string;
        focus: string;
      }
    | any;
  text:
    | {
        main: string;
        focus: string;
        primary?: string;
        secondary?: string;
        disabled?: string;
      }
    | any;
  transparent:
    | {
        main: string;
      }
    | any;
  black:
    | {
        light: string;
        main: string;
        focus: string;
      }
    | any;
  primary: ColorsTypes | any;
  secondary: ColorsTypes | any;
  info: ColorsTypes | any;
  success: ColorsTypes | any;
  warning: ColorsTypes | any;
  error: ColorsTypes | any;
  light: ColorsTypes | any;
  dark: ColorsTypes | any;
  grey:
    | {
        [key: string | number]: string;
      }
    | any;
  // Couleurs Hopelapp Brand
  hopelapp: {
    teal: HopelappBrandTypes;
    gold: HopelappBrandTypes;
    mint: HopelappBrandTypes;
    peach: HopelappBrandTypes;
    coral: HopelappBrandTypes;
    darkGreen: HopelappBrandTypes;
  };
  gradients:
    | {
        primary: GradientsTypes;
        secondary: GradientsTypes;
        info: GradientsTypes;
        success: GradientsTypes;
        warning: GradientsTypes;
        error: GradientsTypes;
        light: GradientsTypes;
        dark: GradientsTypes;
        // Gradients Hopelapp
        teal: GradientsTypes;
        gold: GradientsTypes;
        coral: GradientsTypes;
      }
    | any;
  socialMediaColors:
    | {
        facebook: SocialMediaColorsTypes;
        twitter: SocialMediaColorsTypes;
        instagram: SocialMediaColorsTypes;
        linkedin: SocialMediaColorsTypes;
        pinterest: SocialMediaColorsTypes;
        youtube: SocialMediaColorsTypes;
        vimeo: SocialMediaColorsTypes;
        slack: SocialMediaColorsTypes;
        dribbble: SocialMediaColorsTypes;
        github: SocialMediaColorsTypes;
        reddit: SocialMediaColorsTypes;
        tumblr: SocialMediaColorsTypes;
      }
    | any;
  badgeColors:
    | {
        primary: BadgeColorsTypes;
        secondary: BadgeColorsTypes;
        info: BadgeColorsTypes;
        success: BadgeColorsTypes;
        warning: BadgeColorsTypes;
        error: BadgeColorsTypes;
        light: BadgeColorsTypes;
        dark: BadgeColorsTypes;
        // Badges Hopelapp
        teal: BadgeColorsTypes;
        gold: BadgeColorsTypes;
        coral: BadgeColorsTypes;
      }
    | any;
  coloredShadows:
    | {
        [key: string]: string;
      }
    | any;
  inputBorderColor: string;
  tabs:
    | {
        indicator:
          | {
              boxShadow: string;
            }
          | any;
      }
    | any;
  border: {
    light: string;
    main: string;
    dark: string;
  };
}

const colors: Types = {
  // ==========================================
  // BACKGROUNDS
  // ==========================================
  background: {
    default: hopelappPalette.background.default,
    paper: hopelappPalette.background.paper,
    card: hopelappPalette.background.card,
    sidenav: hopelappPalette.background.sidebar,
    hover: hopelappPalette.background.hover,
  },

  // ==========================================
  // TEXTES
  // ==========================================
  text: {
    main: hopelappPalette.text.secondary,
    focus: hopelappPalette.text.secondary,
    primary: hopelappPalette.text.primary,
    secondary: hopelappPalette.text.secondary,
    disabled: hopelappPalette.text.disabled,
  },

  transparent: {
    main: "transparent",
  },

  white: {
    main: "#ffffff",
    focus: "#ffffff",
  },

  black: {
    light: "#000000",
    main: "#000000",
    focus: "#000000",
  },

  // ==========================================
  // COULEURS SÉMANTIQUES (mappées sur Hopelapp)
  // ==========================================
  primary: {
    main: hopelappPalette.semantic.primary, // #357f84 - Teal
    focus: hopelappPalette.brand.teal.dark,
  },

  secondary: {
    main: hopelappPalette.semantic.secondary,
    focus: "#8f93a9",
  },

  info: {
    main: hopelappPalette.semantic.info, // #357f84 - Teal
    focus: hopelappPalette.brand.teal.light,
  },

  success: {
    main: hopelappPalette.semantic.success,
    focus: "#67bb6a",
  },

  warning: {
    main: hopelappPalette.semantic.warning, // #e3b025 - Gold
    focus: hopelappPalette.brand.gold.light,
  },

  error: {
    main: "#F44335", // Rouge standard pour les erreurs critiques
    focus: "#f65f53",
  },

  light: {
    main: hopelappPalette.semantic.light, // #cde4dc - Mint
    focus: hopelappPalette.brand.mint.light,
  },

  dark: {
    main: hopelappPalette.semantic.dark, // #162117 - Dark green
    focus: hopelappPalette.brand.darkGreen.light,
  },

  // ==========================================
  // COULEURS HOPELAPP BRAND
  // ==========================================
  hopelapp: {
    teal: hopelappPalette.brand.teal,
    gold: hopelappPalette.brand.gold,
    mint: hopelappPalette.brand.mint,
    peach: hopelappPalette.brand.peach,
    coral: hopelappPalette.brand.coral,
    darkGreen: hopelappPalette.brand.darkGreen,
  },

  // ==========================================
  // GRIS
  // ==========================================
  grey: {
    100: "#f8f9fa",
    200: "#f0f2f5",
    300: "#dee2e6",
    400: "#ced4da",
    500: "#adb5bd",
    600: "#6c757d",
    700: "#495057",
    800: "#343a40",
    900: "#212529",
  },

  // ==========================================
  // BORDURES
  // ==========================================
  border: {
    light: hopelappPalette.border.light,
    main: hopelappPalette.border.main,
    dark: hopelappPalette.border.dark,
  },

  // ==========================================
  // GRADIENTS
  // ==========================================
  gradients: {
    primary: {
      main: hopelappPalette.brand.teal.main,
      state: hopelappPalette.brand.teal.dark,
    },

    secondary: {
      main: "#747b8a",
      state: "#495361",
    },

    info: {
      main: hopelappPalette.brand.teal.light,
      state: hopelappPalette.brand.teal.main,
    },

    success: {
      main: "#66BB6A",
      state: "#43A047",
    },

    warning: {
      main: hopelappPalette.brand.gold.light,
      state: hopelappPalette.brand.gold.main,
    },

    error: {
      main: "#EF5350",
      state: "#E53935",
    },

    light: {
      main: hopelappPalette.brand.mint.light,
      state: hopelappPalette.brand.mint.main,
    },

    dark: {
      main: hopelappPalette.brand.darkGreen.light,
      state: hopelappPalette.brand.darkGreen.main,
    },

    default: {
      main: "#a9aec0",
      state: "#f3f5f6",
    },

    // Gradients Hopelapp spécifiques
    teal: {
      main: hopelappPalette.brand.teal.light,
      state: hopelappPalette.brand.teal.dark,
    },

    gold: {
      main: hopelappPalette.brand.gold.light,
      state: hopelappPalette.brand.gold.dark,
    },

    coral: {
      main: hopelappPalette.brand.coral.light,
      state: hopelappPalette.brand.coral.dark,
    },
  },

  // ==========================================
  // SOCIAL MEDIA
  // ==========================================
  socialMediaColors: {
    facebook: {
      main: "#3b5998",
      dark: "#344e86",
    },

    twitter: {
      main: "#55acee",
      dark: "#3ea1ec",
    },

    instagram: {
      main: "#125688",
      dark: "#0e456d",
    },

    linkedin: {
      main: "#0077b5",
      dark: "#00669c",
    },

    pinterest: {
      main: "#cc2127",
      dark: "#b21d22",
    },

    youtube: {
      main: "#e52d27",
      dark: "#d41f1a",
    },

    vimeo: {
      main: "#1ab7ea",
      dark: "#13a3d2",
    },

    slack: {
      main: "#3aaf85",
      dark: "#329874",
    },

    dribbble: {
      main: "#ea4c89",
      dark: "#e73177",
    },

    github: {
      main: "#24292e",
      dark: "#171a1d",
    },

    reddit: {
      main: "#ff4500",
      dark: "#e03d00",
    },

    tumblr: {
      main: "#35465c",
      dark: "#2a3749",
    },
  },

  // ==========================================
  // BADGES
  // ==========================================
  badgeColors: {
    primary: {
      background: hopelappPalette.brand.mint.main,
      text: hopelappPalette.brand.teal.dark,
    },

    secondary: {
      background: "#d7d9e1",
      text: "#6c757d",
    },

    info: {
      background: hopelappPalette.brand.mint.light,
      text: hopelappPalette.brand.teal.main,
    },

    success: {
      background: "#bce2be",
      text: "#339537",
    },

    warning: {
      background: hopelappPalette.brand.gold.light,
      text: hopelappPalette.brand.gold.dark,
    },

    error: {
      background: "#fcd3d0",
      text: "#f61200",
    },

    light: {
      background: "#ffffff",
      text: "#c7d3de",
    },

    dark: {
      background: hopelappPalette.brand.darkGreen.light,
      text: hopelappPalette.brand.darkGreen.main,
    },

    // Badges Hopelapp
    teal: {
      background: hopelappPalette.brand.mint.main,
      text: hopelappPalette.brand.teal.main,
    },

    gold: {
      background: hopelappPalette.brand.peach.main,
      text: hopelappPalette.brand.gold.dark,
    },

    coral: {
      background: hopelappPalette.brand.peach.light,
      text: hopelappPalette.brand.coral.dark,
    },
  },

  // ==========================================
  // SHADOWS
  // ==========================================
  coloredShadows: {
    primary: hopelappPalette.brand.teal.main,
    secondary: "#110e0e",
    info: hopelappPalette.brand.teal.light,
    success: "#4caf4f",
    warning: hopelappPalette.brand.gold.main,
    error: "#f44336",
    light: "#adb5bd",
    dark: hopelappPalette.brand.darkGreen.main,
    teal: hopelappPalette.brand.teal.main,
    gold: hopelappPalette.brand.gold.main,
    coral: hopelappPalette.brand.coral.main,
  },

  inputBorderColor: hopelappPalette.border.main,

  tabs: {
    indicator: { boxShadow: hopelappPalette.brand.teal.main },
  },
};

export default colors;
